import React, {useState, useEffect, useRef, type ChangeEvent} from 'react'
import {formatPhoneForInput, normalizeToE164} from '@/Utils/Phone'

interface Props {
    id?: string
    'data-testid'?: string
    'aria-invalid'?: boolean
    'aria-describedby'?: string
    value: string
    onChange: (value: string) => void
    className?: string
    disabled?: boolean
}

export default function PhoneNumberInput({
    id,
    'data-testid': testId,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedby,
    value,
    onChange,
    className,
    disabled,
}: Props) {
    const initialDisplay = value ? formatPhoneForInput(value) : ''
    const [display, setDisplay] = useState(initialDisplay)
    // Ref tracks the current display value without causing effect re-runs,
    // so we can tell whether a value change came from our own onChange or externally.
    const displayRef = useRef(initialDisplay)

    function updateDisplay(next: string) {
        setDisplay(next)
        displayRef.current = next
    }

    useEffect(() => {
        if (!value) {
            updateDisplay('')
            return
        }
        // Only reformat display when the external value represents a different number
        // than what the user is currently typing. This prevents mid-typing reformatting
        // while still keeping the display in sync when the value changes externally
        // (e.g. switching ticket items resets the form).
        if (normalizeToE164(displayRef.current) !== value) {
            updateDisplay(formatPhoneForInput(value))
        }
    }, [value])

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value
        updateDisplay(raw)
        const e164 = normalizeToE164(raw)
        onChange(e164 ?? raw)
    }

    function handleBlur() {
        const e164 = normalizeToE164(display)
        if (e164) {
            updateDisplay(formatPhoneForInput(display))
            onChange(e164)
        }
    }

    return (
        <input
            type="tel"
            autoComplete="tel"
            id={id}
            data-testid={testId}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedby}
            value={display}
            onChange={handleChange}
            onBlur={handleBlur}
            className={className}
            disabled={disabled}
            placeholder="(555) 123-4567"
        />
    )
}
